import { CheckBox, CheckBoxOutlineBlank, Search } from '@mui/icons-material';
import { Box, Checkbox, InputAdornment, Link } from '@mui/material';
import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { Dish } from '../../../../apiTypes';
import FormAutocomplete from '../../../../components/forms/FormAutocomplete';
import FormSelect from '../../../../components/forms/FormSelect';
import FormSwitch from '../../../../components/forms/FormSwitch';
import FormTextField from '../../../../components/forms/FormTextField';
import { t } from '../../../../constants/translations';
import { useAdmin } from '../../../../contexts/admin.context';
import { useCafe } from '../../../../contexts/cafe.context';
import { useLocale } from '../../../../contexts/locale.context';
import { getInitMap } from '../../../../utils/map';
import useTaxes, { CheckboxTax } from './useTaxes';

const EditDishTaxInfo = () => {
  const { cafe } = useAdmin();
  const { lang } = useLocale();
  const { legalEntities, legalEntitiesMap, categoriesMap } = useCafe();

  const { watch } = useFormContext<Dish>();

  const legalEntityIds = useMemo(() => legalEntities.map((i) => i.id), [legalEntities]);
  const legalEntityId = watch('taxInfo.legalEntityId');
  const categoryId = watch('categoryId');

  const legalEntity = legalEntitiesMap[legalEntityId];

  const { taxes } = useTaxes(legalEntity?.prroCheckBox.key);
  const allTaxCodes = useMemo(() => taxes.map((t) => String(t.code)), [taxes]);
  const allTaxesMap = useMemo(
    () => taxes.reduce((acc, t) => ({ ...acc, [t.code]: t }), getInitMap<CheckboxTax>()),
    [taxes]
  );

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <FormSelect
          label={t.legal_entity[lang]}
          name="taxInfo.legalEntityId"
          options={legalEntityIds}
          renderOption={(id) => legalEntitiesMap[id]?.name || id}
          size="small"
        />
      </Box>
      {cafe?.country === 'ua' && (
        <>
          <Box sx={{ mb: 2 }}>
            <FormAutocomplete
              name="taxInfo.checboxTaxCodes"
              label="Код податкової ставки"
              defaultValue={[]}
              options={allTaxCodes}
              disabled={!legalEntity?.prroCheckBox.key || taxes.length === 0}
              helperText={
                // eslint-disable-next-line no-nested-ternary
                !legalEntity?.prroCheckBox.key
                  ? `Для того, щоб обрати податкові ставки, введіть ключ ліценції каси для юридичної особи ${
                      legalEntity?.name || ''
                    }`
                  : taxes.length === 0
                  ? 'Ви ще не маєте податкових ставок. Налаштуйте податкові ставки в особистому кабінеті checkbox'
                  : undefined
              }
              multiple
              getOptionLabel={(o) => allTaxesMap[o]?.label || 'невідомо'}
              renderOption={(props, o, { selected }) => (
                <li {...props}>
                  <Checkbox
                    icon={<CheckBoxOutlineBlank fontSize="small" />}
                    checkedIcon={<CheckBox fontSize="small" />}
                    style={{ marginRight: 8 }}
                    checked={selected}
                  />
                  {allTaxesMap[o]?.label || 'невідомо'}
                </li>
              )}
              size="small"
            />
          </Box>

          <FormTextField sx={{ mb: 2 }} label="Штрихкод товару" name="taxInfo.barcode" size="small" fullWidth />

          <FormTextField
            sx={{ mb: 1 }}
            label="УКТЗЕД"
            name="taxInfo.uktzed"
            size="small"
            helperText="Обов'язковий для позицій, які містять алкоголь"
            fullWidth
            InputProps={{
              endAdornment: (
                <Link
                  href={`https://export.gov.ua/uktzed_search_tool?search_term=${categoriesMap[categoryId]?.name}#list`}
                  target="_blank"
                  rel="noreferrer"
                >
                  <InputAdornment position="end">
                    <Search />
                  </InputAdornment>
                </Link>
              ),
            }}
          />

          <FormSwitch checkbox name="taxInfo.hasExcise" label="Ця позиція містить продукти з акцизною маркою" />
        </>
      )}
    </>
  );
};

export default EditDishTaxInfo;
